<script setup>
import { WdsButtonPrimary, WdsIllustration, WdsInputField } from '@wds/components';
import { validEmail } from '@/lib/validation';

const { $tracking } = useNuxtApp();
const { useUrl } = useUrls();
const { t } = useI18n();

// ### VARIABLES INITIALIZATION
const userEmail = ref('');
const successEmail = ref(false);
const submittedNewsletter = ref(false);
const errorMessage = t('t_ENTERVALIDEMAILADDRESS');
const successMessage = t('t_THANKYOU');

// ### COMPUTED
const newsletterSignUpSubtitle = computed(() => {
  const privacyNoticeLink = `<a href="${useUrl.getSecurityPrivacyUrl()}">${t('t_DATAPROTECTIONNOTICENAME').toLowerCase()}</a>`;
  return t('t_SIGNUPNEWS', { PRIVACYNOTICELINK: privacyNoticeLink });
});

const newsletterValidation = computed(() => {
  return submittedNewsletter.value ? (successEmail?.value ? 'success' : 'error') : null;
});

const newsletterValidationMessage = computed(() => {
  return submittedNewsletter.value ? (successEmail?.value ? successMessage : errorMessage) : null;
});

// ### METHODS
function handleSubmitNewsletter() {
  submittedNewsletter.value = true;
  successEmail.value = validEmail(userEmail?.value);
  if (!successEmail?.value) {
    return;
  }

  const trackingInfo = {
    segment: {
      event_name: 'Homepage Event',
      action: 'Newsletter Signup Clicked',
      email: userEmail?.value,
      optin_status: true,
    },
  };
  $tracking?.onNewsletterSubmit(trackingInfo.segment);
}

function setUserEmail(value) {
  userEmail.value = value;
}
</script>

<template>
  <section class="newsletter-section">
    <div class="header">
      <WdsIllustration
        class="header-icon"
        asset="mail"
        folder="md"
        loading="lazy"
        :alt="$t('t_JOINOURADVE')"
        :title="$t('t_JOINOURADVE')"
      />
      <div class="header-content">
        <h2 class="title">
          {{ $t('t_JOINOURADVE') }}
        </h2>

        <h3
          class="sub-title"
          v-html="newsletterSignUpSubtitle"
        />
      </div>
    </div>
    <WdsInputField
      type="text"
      class="subscription-input"
      debounce="1000"
      prefix-icon="send"
      :value="userEmail"
      :label="$t('t_YOUREMAIL')"
      :clear-on-focus="false"
      :status="newsletterValidation"
      :helper-text="newsletterValidationMessage"
      @input-value="setUserEmail"
    >
      <template #input-suffix>
        <div class="input-suffix-container">
          <WdsButtonPrimary
            icon-start="arrow-right"
            :aria-label="$t('t_NEWSLETTERSUBSCRIPTIONBUTTONARIALABEL')"
            @click="handleSubmitNewsletter"
          />
        </div>
      </template>
    </WdsInputField>
    <p
      class="monitoring-message"
      v-html="t('t_NEWSLETTERMONITORING', { SECURITYPRIVACYURL: useUrl.getSecurityPrivacyUrl() })"
    />
  </section>
</template>

<style lang="scss" scoped>
/* VARIABLES */
$footer-top-shape-height: $wds-spacing-xxl;

@include desktop {
  $footer-top-shape-height: $wds-spacing-xxxl;
}

/* END VARIABLES */

.newsletter-section {
  margin-bottom: -$wds-spacing-xxl;
  clip-path: polygon(0 0, 100% 0%, 100% calc(100% - #{$footer-top-shape-height}), 0 100%);
  background-color: $wds-color-teal-light;
  padding: wds-rem(40px) $wds-spacing-m calc(#{$footer-top-shape-height} + #{$wds-spacing-xxl}) $wds-spacing-m;
  display: grid;
  gap: $wds-spacing-m;

  :deep(a) {
    text-decoration: underline;

    &:focus-visible {
      outline: $wds-spacing-xxs solid $wds-color-ink-darker !important;
      outline-offset: $wds-spacing-xxs;
      border-radius: $wds-spacing-xs;
    }
  }

  .header,
  .subscription-input {
    width: 100%;
    max-width: wds-rem(600px);
    margin: 0 auto;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: $wds-spacing-m;

    &-icon {
      display: none;
    }

    &-content {
      .title {
        @include title-4-bld;

        color: $wds-color-ink-darker;
      }

      .sub-title {
        @include body-2-reg;

        color: $wds-color-ink-darker;
      }
    }
  }

  .monitoring-message {
    color: $wds-color-teal-darker;
    text-align: center;
    max-width: wds-rem(625px);
    margin: 0 auto;

    @include body-3-reg;

    :deep(a) {
      color: $wds-color-teal-darker;
    }
  }

  @include tablet-large {
    .header {
      &-icon {
        display: block;
        place-self: start;
      }

      &-content {
        .title {
          @include title-3-bld;
        }
      }
    }
  }

  @include desktop {
    grid-template-rows: auto auto;
    grid-template-columns: auto wds-rem(400px);
    place-content: center;

    .subscription-input {
      max-width: wds-rem(400px);
    }

    .monitoring-message {
      grid-row: 2;
      grid-column: 1 / span 2;
      width: wds-rem(800px);
    }
  }

  .input-suffix-container {
    padding-right: $wds-spacing-xxs;
  }
}
</style>
